import React, { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import { showSnackbar } from '../../../api/snackbarSlice';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { TIME_PERIOD_PRESETS } from '../../documentReports/constants/documentReportFilterConstants';
import ReportTimePeriodFilter from '../../documentReports/components/filters/ReportTimePeriodFilter';
import { computeTimePeriodDates, getTimePeriodDayCount } from '../../documentReports/utils/timePeriodUtils';
import { auditClient } from '../../../utils/axios';

const MAX_RANGE_DAYS = 90;

const defaultTimePeriod = () => {
  const preset = TIME_PERIOD_PRESETS.LAST_7_DAYS;

  return { preset, ...computeTimePeriodDates(preset) };
};

const exportErrorMessage = async (error, messages) => {
  if (error?.response?.status === 403) {
    return messages.permission_error;
  }

  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.errors?.message || parsed?.message || messages.export_error;
    } catch {
      return messages.export_error;
    }
  }

  return data?.message || data?.errors?.message || messages.export_error;
};

const fileNameFor = (startDate, endDate) => `logs_${startDate}_to_${endDate}.xlsx`;

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const exportRange = async (startDate, endDate) => {
  const { data } = await auditClient.get('/logs/export', {
    params: {
      start_time: `${startDate}T00:00:00.000Z`,
      end_time: `${endDate}T23:59:59.000Z`,
    },
    responseType: 'blob',
    skipErrorSnackbar: true,
  });

  downloadBlob(data, fileNameFor(startDate, endDate));
};

const AuditLogSection = () => {
  const dispatch = useDispatch();
  const { settings, documentReports } = useTranslatedMessages();
  const messages = settings.auditLog || {};
  const filterLabels = documentReports?.filters ?? {};
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [timePeriod, setTimePeriod] = useState(defaultTimePeriod);

  const dayCount = useMemo(
    () => getTimePeriodDayCount(timePeriod.startDate, timePeriod.endDate),
    [timePeriod.endDate, timePeriod.startDate],
  );
  const rangeInvalid = dayCount <= 0 || dayCount > MAX_RANGE_DAYS;

  const daysSelectedText = (filterLabels.days_selected ?? '{count} days selected (max {max})')
    .replace('{count}', String(dayCount))
    .replace('{max}', String(MAX_RANGE_DAYS));

  const openExportDialog = () => {
    setTimePeriod(defaultTimePeriod());
    window.setTimeout(() => setDialogOpen(true), 0);
  };

  const handleExport = async () => {
    if (rangeInvalid) {
      return;
    }

    const startValue = dayjs(timePeriod.startDate).format('YYYY-MM-DD');
    const endValue = dayjs(timePeriod.endDate).format('YYYY-MM-DD');
    setCreating(true);
    try {
      await exportRange(startValue, endValue);
      setDialogOpen(false);
      dispatch(
        showSnackbar({
          message: messages.export_success,
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: await exportErrorMessage(error, messages),
          severity: 'error',
        }),
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {messages.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {messages.subtitle}
          </Typography>
        </Box>
        <Button
          id="export-audit-logs"
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={openExportDialog}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {messages.export_button}
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => !creating && setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        disableEnforceFocus
      >
        <DialogTitle>{messages.dialog_title}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <ReportTimePeriodFilter
              id="audit-log-time-period"
              value={timePeriod}
              onChange={setTimePeriod}
              required
              error={rangeInvalid}
              width="100%"
            />
            <Typography
              variant="caption"
              color={rangeInvalid ? 'error' : 'text.secondary'}
              sx={{ mt: 0.5, display: 'block' }}
            >
              {dayCount > 0
                ? daysSelectedText
                : (filterLabels.select_period ?? `Select a time period (max ${MAX_RANGE_DAYS} days)`)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            {messages.cancel}
          </Button>
          <Button variant="contained" disabled={rangeInvalid || creating} onClick={handleExport}>
            {creating ? messages.downloading : messages.generate}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogSection;
